import React, { useState } from "react";
import styles from "./styles.module.scss";
import { useParams } from "react-router-dom";
// Components
import AltSpinner from "../Spinners/AltSpinner";
import SecondaryButton from "../SecondaryButton";
import Sensor from "../Sensor";
import { EditIcon } from "../../Styles/Icons";
// Hooks
import {
  GetHardwareMonitoringStatusBody,
  postHubProperty,
} from "@intelligentlilli/api-layer";
// State
import { setLoading } from "../../State/slices/session-slice";
import { updateServiceUser } from "../../State/slices/serviceUsers-slice";
import { useAugmentDeviceData } from "../../Services/Utils/newDeviceMonitoring/useAugmentDeviceData";
import LilliButton from "../DesignSystemComponents/LilliButton/LilliButton"; // Redux
import { useAppDispatch, useAppSelector } from "State/hooks";
import { useFade } from "Services/Hooks";
import NewStyleModal from "Components/NewStyleModal";
import Troubleshooting from "Components/InstallationNew/Troubleshooting";
import { useViewport } from "Components/ViewportProvider";
import MobileDrawer from "Components/MobileDrawer";
import InstallationNotes from "./components/InstallationNotes";
import { getTroubleshootingProps } from "./components/utils/getTroubleshootingProps";
import { isDeviceOffline } from "./components/utils/utils";
import {
  DeviceType,
  SensorStatus,
} from "Services/Utils/newDeviceMonitoring/types";
import { determineSensorStatus } from "Services/Utils/newDeviceMonitoring/determineSensorStatus";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";

interface DeviceOverviewProps {
  serviceUser: any;
  indexOfSU: number;
  sensorData: {
    loading: boolean;
    error: boolean;
    data: GetHardwareMonitoringStatusBody | null;
    getHardwareStatus: () => void;
    sensorStatus: SensorStatus;
  };
}

const DevicesOverview = ({
  serviceUser,
  indexOfSU,
  sensorData,
}: DeviceOverviewProps) => {
  const { id } = useParams(); // Get the service user id from the url parameter

  const track = useAmplitudeTracking();

  const trackDevices = (eventProperties: Record<string, string>) => {
    track("Device Monitoring", { ...eventProperties });
  };

  const trackInstallNotes = (eventProperties: Record<string, string>) => {
    track("Install Notes", eventProperties);
  };

  const { width } = useViewport();
  const isDesktop = width >= 900;
  const dispatch = useAppDispatch();
  const server = useAppSelector((state) => state.session.server);

  // DEVICE MONITORING

  // data comes directly from device monitoring endpoint 200 response
  // this has been seen in dev to come back as an empty array.
  // we protect the front end device cards with an empty array conditional check
  const { data, error, loading, getHardwareStatus } = sensorData;

  // augment the data with sensor labels and photos
  const augmentedSensors = useAugmentDeviceData(data, Number(id));

  // Extracting sensors/devices from the sensors list
  const sensorsExcludingHub = augmentedSensors?.filter(
    (sensor) => sensor.child_device_internal_id !== "hub"
  );

  const hub = augmentedSensors?.find(
    (sensor) => sensor.child_device_internal_id === "hub"
  );

  // currently, the device monitoring endpoint can return current_status === "down" for the hub while sensors are online
  // thus we can assume the hub actually IS online. it returns current status === "down" when it hasn't transmitted data in the last hour
  // to prevent "inaccuracy" on the front end UI, we want to show that if sensors are online that the hub actually is online

  // are any sensors online
  // then the hub is online and transmitting data, but hasn't transmitted data in the last hour
  const areSomeSensorsOnline = sensorsExcludingHub?.some(
    (sensor) => sensor.current_status === "up"
  );

  if (hub && areSomeSensorsOnline) {
    hub.current_status = "up";
  }

  // Remove any sensors from the list that have been removed
  const activeDevices = sensorsExcludingHub?.filter(
    (device) => !device?.location?.includes("REMOVED")
  );

  const isHubOnline = hub?.current_status === "up";

  // TROUBLESHOOTING
  const [
    isTroubleshootingVisible,
    setShowTroubleshootingAnimation,
    showTroubleshootingAnimation,
  ] = useFade(false, 150);

  const [troubleshootingDevice, setTroublshootingDevice] =
    useState<DeviceType>("Hub");

  const [helpChoice, setHelpChoice] = useState(null);

  const troubleshootingProps = getTroubleshootingProps(troubleshootingDevice);

  // NOTES
  const [editingNotes, setEditingNotes] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [notes, setNotes] = useState<string>(
    serviceUser?.serviceUserProperties?.installationNotes || ""
  );

  // Update installation notes
  const updateNotes = (note: string) => {
    dispatch(setLoading(true));
    postHubProperty(server, Number(id), "InstallationNotes", note, "web")
      .then((res) => {
        if (res.ok) {
          // Update redux notes for the service user
          dispatch(
            updateServiceUser({
              index: indexOfSU,
              key: "serviceUserProperties",
              value: {
                ...serviceUser?.serviceUserProperties,
                installationNotes: notes,
              },
            })
          );
          // If the API call previously failed and now works cancel the error message
          setShowErrorMessage(false);
          // Close the editing box
          setEditingNotes(false);

          trackInstallNotes({ action: "form_submitted" });
        } else {
          setShowErrorMessage(true);
          setNotes(serviceUser?.serviceUserProperties?.installationNotes || "");
        }
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
        setShowErrorMessage(true);
        setNotes(serviceUser?.serviceUserProperties?.installationNotes || "");
      });
  };

  return (
    <div className={styles.card}>
      <h1 className={styles.heading}>Devices</h1>
      <div>
        <InstallationNotes
          setEditingNotes={setEditingNotes}
          notes={notes}
          showErrorMessage={showErrorMessage}
          updateNotes={updateNotes}
          setNotes={setNotes}
          serviceUser={serviceUser}
          editingNotes={editingNotes}
          setShowErrorMessage={setShowErrorMessage}
        />
        <div className={styles.buttons}>
          {!editingNotes ? (
            <SecondaryButton
              style={{ fontSize: 16 }}
              startIcon={<EditIcon style={{ width: 16 }} />}
              onClick={() => {
                setEditingNotes(true);
                trackInstallNotes({ action: "form_opened" });
              }}
            >
              Edit Installation notes
            </SecondaryButton>
          ) : (
            <div />
          )}
          <div className={styles.sensor_button_group}>
            <LilliButton
              onClick={() => {
                getHardwareStatus();
                trackDevices({ action: "connection_tested" });
              }}
              disabled={loading}
            >
              {loading ? "Testing..." : "Test connection"}
            </LilliButton>
          </div>
        </div>
      </div>
      {loading && (
        <div className={styles.loading_sensors}>
          <AltSpinner style={{ alignSelf: "center" }} />
          <div>Connecting to sensors</div>
        </div>
      )}
      {(!loading || augmentedSensors?.length !== 0) && (
        <div className={styles.devices}>
          {/* MAIN HUB */}
          {hub ? (
            <Sensor
              device={hub}
              isOffline={!isHubOnline}
              sensorStatus={determineSensorStatus(hub, isHubOnline, isDesktop)}
              onClick={
                !isHubOnline
                  ? () => {
                      setTroublshootingDevice("Hub"); // Store the hub as the troubleshooting device
                      setShowTroubleshootingAnimation(true);
                      trackDevices({ action: "guide_clicked", sensor: "hub" });
                    }
                  : undefined
              }
            />
          ) : null}

          {/* SENSORS */}
          {activeDevices?.map((device, index) => {
            const deviceStatus = determineSensorStatus(
              device,
              isHubOnline,
              isDesktop
            );

            return (
              <Sensor
                device={device}
                isOffline={device.current_status === "down"}
                sensorStatus={deviceStatus}
                key={index}
                onClick={
                  isDeviceOffline(device) && isHubOnline && device.type
                    ? () => {
                        setTroublshootingDevice(device.type!); // Store the clicked device for troubleshooting
                        setShowTroubleshootingAnimation(true);
                        trackDevices({
                          action: "guide_clicked",
                          sensor: "sensor",
                        });
                      }
                    : undefined
                }
              />
            );
          })}
        </div>
      )}
      {!loading && (error || augmentedSensors?.length === 0) && (
        <div>
          Sorry, we could not fetch sensor data. You can either try again or
          contact us on +44 (0)758 800 8527 or email
          support@intelligentlilli.com
        </div>
      )}

      {isTroubleshootingVisible && isDesktop ? (
        <NewStyleModal
          hide={() => {
            setHelpChoice(null);
            setShowTroubleshootingAnimation(false);
          }}
          showCloseIcon={true}
          showAnimation={showTroubleshootingAnimation}
          size={"medium"}
        >
          <Troubleshooting
            isDesktop={isDesktop}
            setShowAnimation={setShowTroubleshootingAnimation}
            isVisisble={isTroubleshootingVisible}
            setHelpChoice={setHelpChoice}
            helpChoice={helpChoice}
            isInstall={false}
            {...troubleshootingProps}
          />
        </NewStyleModal>
      ) : (
        <MobileDrawer
          closeModal={() => {
            setHelpChoice(null);
            setShowTroubleshootingAnimation(false);
          }}
          showGuide={showTroubleshootingAnimation}
        >
          <Troubleshooting
            isDesktop={isDesktop}
            setShowAnimation={setShowTroubleshootingAnimation}
            isVisisble={isTroubleshootingVisible}
            setHelpChoice={setHelpChoice}
            helpChoice={helpChoice}
            isInstall={false}
            {...troubleshootingProps}
          />
        </MobileDrawer>
      )}
    </div>
  );
};

export default DevicesOverview;
